import { Card, Grow, Skeleton } from "@mui/material";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { OperationalBacklogBucket } from "@/services/service-dashboard.service";

import { DashboardColor, pulseSx, staggerDelay, surfaceCardSx } from "../styles";

type Props = {
  title: string;
  items: OperationalBacklogBucket[];
  emptyLabel: string;
  isLoading?: boolean;
  isRefreshing?: boolean;
  /** CSS colors per range; when omitted the theme palette mapping below is used. */
  rangeColors?: Record<string, string>;
};

const backlogColor = (range: string): DashboardColor => {
  if (range === "0-15") return "success";
  if (range === "15-30") return "warning";
  if (range === "30-60") return "error";
  return "info";
};

const formatPercent = (value: number): string =>
  `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 1 }).format(value)}%`;

const BacklogCard = ({
  title,
  items,
  emptyLabel,
  isLoading = false,
  isRefreshing = false,
  rangeColors,
}: Props) => {
  return (
    <Card sx={surfaceCardSx("warning")}>
      <MDBox p={2}>
        <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
          {title}
        </MDTypography>
        {isLoading ? (
          <>
            {[0, 1, 2, 3].map((item) => (
              <MDBox key={item} mb={1.5}>
                <Skeleton variant="text" width="40%" height={16} />
                <Skeleton variant="rounded" width="100%" height={10} />
              </MDBox>
            ))}
          </>
        ) : items.length === 0 ? (
          <MDTypography variant="button" color="text">
            {emptyLabel}
          </MDTypography>
        ) : (
          <MDBox sx={pulseSx(isRefreshing)}>
            {items.map((bucket, index) => (
              <Grow
                in
                timeout={520}
                style={{ transitionDelay: staggerDelay(index) }}
                key={bucket.range}
              >
                <MDBox mb={1.25}>
                  <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                    <MDTypography variant="caption" fontWeight="medium">
                      {bucket.range} min
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      <MDTypography variant="caption" fontWeight="bold" component="span">
                        {bucket.total}
                      </MDTypography>
                      &nbsp;({formatPercent(bucket.percentage)})
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    width="100%"
                    height="0.375rem"
                    borderRadius="lg"
                    bgColor="light"
                    overflow="hidden"
                  >
                    <MDBox
                      variant="gradient"
                      bgColor={backlogColor(bucket.range)}
                      height="100%"
                      borderRadius="lg"
                      width={`${Math.max(0, Math.min(100, bucket.percentage))}%`}
                      sx={{
                        ...(rangeColors?.[bucket.range]
                          ? { background: rangeColors[bucket.range] }
                          : {}),
                        transition: "width 1s cubic-bezier(0.22, 1, 0.36, 1)",
                        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                      }}
                    />
                  </MDBox>
                </MDBox>
              </Grow>
            ))}
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
};

export default BacklogCard;
